import {fabric} from "fabric";
import StorageService from "@/service/StorageService";

export default class ImageEditor {
    idPrefix = '';
    companyId = 0;
    imageEditorCanvas = null;
    setBackgroundMode = false;

    constructor(idPrefix, companyId) {
        console.debug('ImageEditor constructor', idPrefix, companyId);

        this.idPrefix = idPrefix;
        this.companyId = companyId;
    }

    initImageEditor() {
        console.debug('initImageEditor');

        const thisIdPrefix = this.idPrefix;

        const canvas = new fabric.Canvas(thisIdPrefix + 'noteCanvas', {isDrawingMode: false});
        this.imageEditorCanvas = canvas;

        ImageEditor.applySettingsToBrush(canvas, thisIdPrefix);
        ImageEditor.handleIsDrawingModeChanged(canvas, thisIdPrefix);
        ImageEditor.handleSetBackgroundModeChanged(this.setBackgroundMode, thisIdPrefix);

        canvas.on('selection:created', function (options) {
            console.debug('on selection created', options);

            if (canvas.getActiveObject() && (!canvas.getActiveObject()['_objects'])) {
                let removeObject = ImageEditor.elementById(thisIdPrefix, 'remove-object');
                removeObject.disabled = false;
            }
        });

        canvas.on('selection:cleared', function (options) {
            console.debug('on selection cleared', options);

            let removeObject = ImageEditor.elementById(thisIdPrefix, 'remove-object');
            removeObject.disabled = true;
        });

        ImageEditor.elementById(thisIdPrefix, 'drawing-mode-toggle').onclick = this.handleClickDrawingModeToggle;
        ImageEditor.elementById(thisIdPrefix, 'set-background-toggle').onclick = this.handleClickSetBackgroundToggle;

        ImageEditor.elementById(thisIdPrefix, 'remove-object').onclick = function () {
            console.debug('handle on click remove object');

            if (canvas.getActiveObject() && (!canvas.getActiveObject()['_objects'])) {
                canvas.remove(canvas.getActiveObject());
            }
        };

        ImageEditor.elementById(thisIdPrefix, 'clear-canvas').onclick = function () {
            console.debug('handle on click clear-canvas');

            canvas.clear();
        };

        ImageEditor.elementById(thisIdPrefix, 'drawing-mode-selector').onchange = function () {
            console.debug('handle change drawing mode selector to brush', (this.value + 'Brush'));

            canvas.freeDrawingBrush = new fabric[this.value + 'Brush'](canvas);
            if (canvas.freeDrawingBrush) {
                ImageEditor.applySettingsToBrush(canvas, thisIdPrefix);
            }
        };

        ImageEditor.elementById(thisIdPrefix, 'drawing-line-width').onchange = function () {
            canvas.freeDrawingBrush.width = parseInt(this.value, 10);

            this.previousSibling.innerHTML = this.value;
        };

        ImageEditor.elementById(thisIdPrefix, 'drawing-color').onchange = function () {
            const brush = canvas.freeDrawingBrush;

            brush.color = this.value;
            if (brush.getPatternSrc) {
                brush.source = brush.getPatternSrc.call(brush);
            }
        };

        ImageEditor.elementById(thisIdPrefix, 'drawing-shadow-color').onchange = function () {
            canvas.freeDrawingBrush.shadow.color = this.value;
        };

        ImageEditor.elementById(thisIdPrefix, 'drawing-shadow-width').onchange = function () {
            canvas.freeDrawingBrush.shadow.blur = parseInt(this.value, 10);

            this.previousSibling.innerHTML = this.value;
        };

        ImageEditor.elementById(thisIdPrefix, 'drawing-shadow-offset').onchange = function () {
            canvas.freeDrawingBrush.shadow.offsetX = parseInt(this.value, 10);
            canvas.freeDrawingBrush.shadow.offsetY = parseInt(this.value, 10);

            this.previousSibling.innerHTML = this.value;
        };

        ImageEditor.elementById(thisIdPrefix, 'canvas-background-selector').onchange = this.handleCanvasBackgroundImageChanged;
    }

    saveCanvas() {
        const imageData = this.imageEditorCanvas.toDataURL("image/png").replace("img/png", "image/octet-string");
        console.debug('Saving imageData', imageData);

        this.imageEditorCanvas.includeDefaultValues = true;
        const jsonData = JSON.stringify(this.imageEditorCanvas.toJSON(), null, '  ');
        console.debug('Saving jsonData', jsonData);

        return {jsonData: jsonData, imageData: imageData};
    }

    loadCanvas(json) {
        console.debug('loadCanvas');
        this.imageEditorCanvas.loadFromJSON(json, this.imageEditorCanvas.renderAll.bind(this.imageEditorCanvas));
    }

    handleClickDrawingModeToggle = e => {
        console.debug('handleClickDrawingModeToggle', e);

        this.imageEditorCanvas.isDrawingMode = !this.imageEditorCanvas.isDrawingMode;
        ImageEditor.handleIsDrawingModeChanged(this.imageEditorCanvas, this.idPrefix);
    }

    handleClickSetBackgroundToggle = e => {
        console.debug('handleClickSetBackgroundToggle', e);

        this.setBackgroundMode = !this.setBackgroundMode;
        ImageEditor.handleSetBackgroundModeChanged(this.setBackgroundMode, this.idPrefix);
    }

    handleCanvasBackgroundImageChanged = e => {
        console.debug('handleCanvasBackgroundImageChanged', e.target.value);
        ImageEditor.setBackgroundImage(this.imageEditorCanvas, e.target.value, this.companyId);

        this.setBackgroundMode = false;
        ImageEditor.handleSetBackgroundModeChanged(this.setBackgroundMode, this.idPrefix);
    }

    static applySettingsToBrush(canvas, idPrefix) {
        console.debug('applySettingsToBrush', canvas.freeDrawingBrush, idPrefix);

        const brush = canvas.freeDrawingBrush;

        brush.color = ImageEditor.elementValById(idPrefix, 'drawing-color');
        if (brush.getPatternSrc) {
            brush.source = brush.getPatternSrc.call(brush);
        }

        brush.width = parseInt(ImageEditor.elementValById(idPrefix, 'drawing-line-width'), 10);

        const offset = parseInt(ImageEditor.elementValById(idPrefix, 'drawing-shadow-offset'), 10);
        brush.shadow = new fabric.Shadow({
            blur: parseInt(ImageEditor.elementValById(idPrefix, 'drawing-shadow-width'), 10),
            offsetX: offset,
            offsetY: offset,
            affectStroke: true,
            color: ImageEditor.elementValById(idPrefix, 'drawing-shadow-color')
        });

        console.debug('applySettingsToBrush done', canvas.freeDrawingBrush, idPrefix);
    }

    static handleIsDrawingModeChanged(canvas, idPrefix) {
        console.debug('handleIsDrawingModeChanged', canvas.isDrawingMode, idPrefix);

        if (canvas.isDrawingMode) {
            console.debug('Its drawing mode');

            let drawingModeOptions = ImageEditor.elementById(idPrefix, 'drawing-mode-options');
            drawingModeOptions.style.display = 'inline-block';

            let actionButtons = ImageEditor.elementById(idPrefix, 'action-buttons');
            actionButtons.style.display = 'none';

            let setBackgroundToggle = ImageEditor.elementById(idPrefix, 'set-background-toggle');
            setBackgroundToggle.disabled = true;

            let drawingModeToggle = ImageEditor.elementById(idPrefix, 'drawing-mode-toggle');
            drawingModeToggle.style.boxShadow = 'darkgrey 2px 2px 2px 2px';
        } else {
            console.debug('Its NOT drawing mode');

            let drawingModeOptions = ImageEditor.elementById(idPrefix, 'drawing-mode-options');
            drawingModeOptions.style.display = 'none';

            let actionButtons = ImageEditor.elementById(idPrefix, 'action-buttons');
            actionButtons.style.display = 'inline-block';

            let setBackgroundToggle = ImageEditor.elementById(idPrefix, 'set-background-toggle');
            setBackgroundToggle.disabled = false;

            let drawingModeToggle = ImageEditor.elementById(idPrefix, 'drawing-mode-toggle');
            drawingModeToggle.style.boxShadow = '';
        }
    }

    static handleSetBackgroundModeChanged(setBackgroundMode, idPrefix) {
        console.debug('handleSetBackgroundModeChanged', setBackgroundMode, idPrefix);

        if (setBackgroundMode) {
            let setBackgroundOptions = ImageEditor.elementById(idPrefix, 'set-background-options');
            setBackgroundOptions.style.display = 'inline-block';

            let actionButtons = ImageEditor.elementById(idPrefix, 'action-buttons');
            actionButtons.style.display = 'none';

            let drawingModeToggle = ImageEditor.elementById(idPrefix, 'drawing-mode-toggle');
            drawingModeToggle.disabled = true;

            let setBackgroundToggle = ImageEditor.elementById(idPrefix, 'set-background-toggle');
            setBackgroundToggle.style.boxShadow = 'darkgrey 2px 2px 2px 2px';
        } else {
            let setBackgroundOptions = ImageEditor.elementById(idPrefix, 'set-background-options');
            setBackgroundOptions.style.display = 'none';

            let actionButtons = ImageEditor.elementById(idPrefix, 'action-buttons');
            actionButtons.style.display = 'inline-block';

            let drawingModeToggle = ImageEditor.elementById(idPrefix, 'drawing-mode-toggle');
            drawingModeToggle.disabled = false;

            let setBackgroundToggle = ImageEditor.elementById(idPrefix, 'set-background-toggle');
            setBackgroundToggle.style.boxShadow = '';
        }
    }

    static setBackgroundImage(canvas, newValue, companyId) {
        if (newValue === 'Select') {
            console.debug('Ignoring initial value', newValue);
            return;
        }

        const assetId = parseInt(newValue, 10) || 0;

        if (assetId !== 0) {
            StorageService.downloadFile(companyId, assetId).then(response => {
                console.info('Got download file response', response);

                response.data.arrayBuffer().then(buffer => {
                    console.info('Got downloaded file', buffer);

                    const base64 = ImageEditor.bytesToBase64(buffer);
                    const dataUrl = `data:image/octet-stream;base64,${base64}`;

                    fabric.Image.fromURL(dataUrl, function (img) {
                        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));
                    });
                })
            })

            console.debug('handle change background image for non zero value', newValue);
        } else {
            console.debug('clearing background image', newValue);
            canvas.setBackgroundImage(null, canvas.renderAll.bind(canvas));
        }
    }

    static bytesToBase64(buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    static elementById(idPrefix, id) {
        const e = document.getElementById(idPrefix + id);
        console.debug("Found element ", e, idPrefix, id);
        return e;
    }

    static elementValById(idPrefix, id) {
        const e = document.getElementById(idPrefix + id);
        console.debug("Found element value ", e, idPrefix, id);
        return e.value;
    }
}
